import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useTranslation } from "react-i18next";
import { getDashboard, getEarnings, getStoreEarnings } from "../../api/deliveryCompanyService";
import { formatCurrency } from "../Admin/constants";
import { brand } from "./theme";

const OVERVIEW_CARDS = [
  { key: "driverCount", labelKey: "drivers", icon: PeopleAltOutlinedIcon, accent: brand.tealStart },
  { key: "activeDeliveryCount", labelKey: "activeDeliveries", icon: LocalShippingOutlinedIcon, accent: brand.tealEnd },
  { key: "completedDeliveryCount", labelKey: "completedDeliveries", icon: CheckCircleOutlineIcon, accent: brand.forestGreen },
];

const PERIOD_OPTIONS = ["today", "currentMonth", "pastMonth"];

function MoneyCell({ total, count }) {
  const { t } = useTranslation("boShared");
  return (
    <Stack sx={{ lineHeight: 1.1, py: 1 }}>
      <Typography variant="body2" fontWeight={600}>
        {formatCurrency(total)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t("orderCount", { count })}
      </Typography>
    </Stack>
  );
}

function HeroStat({ icon: Icon, label, value, helper, accent }) {
  return (
    <Card
      sx={{
        height: "100%",
        p: 2.5,
        borderRadius: 3,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 12px 28px rgba(11, 43, 38, 0.14)",
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            flexShrink: 0,
            backgroundImage: `linear-gradient(135deg, ${alpha(accent, 0.85)}, ${accent})`,
            boxShadow: `0 6px 16px ${alpha(accent, 0.4)}`,
          }}
        >
          <Icon sx={{ color: "#fff" }} />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: "block",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
            }}
          >
            {label}
          </Typography>
          <Typography variant="h4" noWrap sx={{ mt: 0.25, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 0.25, display: "block" }}>
              {helper}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Avatar variant="rounded" sx={{ bgcolor: "primary.main", width: 34, height: 34, borderRadius: 2 }}>
        <Icon fontSize="small" />
      </Avatar>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export default function Dashboard() {
  const { t } = useTranslation(["dcDashboard", "dcShared", "boShared"]);
  const [period, setPeriod] = useState("currentMonth");
  const [dashboard, setDashboard] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [storeEarnings, setStoreEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getDashboard(period), getEarnings(period), getStoreEarnings(period)])
      .then(([dashboardData, earningsData, storeEarningsData]) => {
        if (!active) return;
        setDashboard(dashboardData);
        setEarnings(earningsData);
        setStoreEarnings(storeEarningsData);
        setError("");
      })
      .catch(() => {
        if (active) setError("loadError");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [period]);

  const periodLabel = t(`dcShared:periods.${period}`);

  const handlePeriodChange = (event, value) => {
    if (value) setPeriod(value);
  };

  const driverColumns = useMemo(
    () => [
      { field: "fullName", headerName: t("dcShared:driver"), flex: 1, minWidth: 160 },
      {
        field: "periodTotal",
        headerName: periodLabel,
        width: 150,
        renderCell: (params) => <MoneyCell total={params.row.periodTotal} count={params.row.periodCount} />,
      },
      {
        field: "allTimeTotal",
        headerName: t("dcDashboard:allTime"),
        width: 130,
        renderCell: (params) => <MoneyCell total={params.row.allTimeTotal} count={params.row.allTimeCount} />,
      },
    ],
    [periodLabel, t]
  );

  const storeColumns = useMemo(
    () => [
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 180, sortable: false },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 100, sortable: false },
      {
        field: "deliveryFeeTotal",
        headerName: t("dcDashboard:deliveryFeeTotal"),
        width: 170,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "commissionPercent",
        headerName: t("dcDashboard:platformFeePercent"),
        width: 130,
        sortable: false,
        valueFormatter: (v) => (v == null ? "" : `${v}%`),
      },
      {
        field: "platformFeeTotal",
        headerName: t("dcDashboard:platformFee"),
        width: 140,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "myEarningsTotal",
        headerName: t("dcDashboard:myEarnings"),
        width: 150,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
    ],
    [t]
  );

  const storeRows = useMemo(() => {
    if (storeEarnings.length === 0) return [];

    const totals = storeEarnings.reduce(
      (acc, s) => ({
        orderCount: acc.orderCount + (s.orderCount || 0),
        deliveryFeeTotal: acc.deliveryFeeTotal + (s.deliveryFeeTotal || 0),
        platformFeeTotal: acc.platformFeeTotal + (s.platformFeeTotal || 0),
        myEarningsTotal: acc.myEarningsTotal + (s.myEarningsTotal || 0),
      }),
      { orderCount: 0, deliveryFeeTotal: 0, platformFeeTotal: 0, myEarningsTotal: 0 }
    );

    return [
      ...storeEarnings,
      {
        storeId: "__total__",
        storeName: t("dcShared:total"),
        commissionPercent: null,
        isTotalRow: true,
        ...totals,
      },
    ];
  }, [storeEarnings, t]);

  const periodToggle = (
    <ToggleButtonGroup
      value={period}
      exclusive
      onChange={handlePeriodChange}
      size="small"
      color="primary"
      sx={{
        bgcolor: "background.paper",
        p: 0.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        "& .MuiToggleButton-root": {
          border: "none",
          borderRadius: "18px !important",
          px: 2,
          fontWeight: 600,
          color: "text.secondary",
        },
        "& .Mui-selected": {
          bgcolor: "primary.main !important",
          color: "#fff !important",
        },
      }}
    >
      {PERIOD_OPTIONS.map((value) => (
        <ToggleButton key={value} value={value}>
          {t(`dcShared:periods.${value}`)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const header = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
      sx={{ mb: 3 }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          variant="rounded"
          sx={{ bgcolor: "primary.main", width: 52, height: 52, borderRadius: 3, display: { xs: "none", sm: "flex" } }}
        >
          <LocalShippingIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
            {t("dcDashboard:title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {dashboard?.companyName
              ? t("dcDashboard:overviewFor", { name: dashboard.companyName })
              : t("dcDashboard:overviewFallback")}
          </Typography>
        </Box>
      </Stack>
      {periodToggle}
    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1320, mx: "auto" }}>
        {header}
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1320, mx: "auto" }}>
        {header}
        <Alert severity="error">{t(`dcDashboard:${error}`)}</Alert>
      </Box>
    );
  }

  if (!dashboard || !earnings) return null;

  return (
    <Box sx={{ maxWidth: 1320, mx: "auto" }}>
      {header}

      {/* Earnings hero banner */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: "none",
          color: "#fff",
          background: `linear-gradient(135deg, ${brand.nightGreen} 0%, ${brand.tealEnd} 60%, ${brand.emeraldLight} 130%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <PaidOutlinedIcon
          sx={{
            position: "absolute",
            right: -18,
            bottom: -18,
            fontSize: 160,
            opacity: 0.08,
          }}
        />
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ position: "relative" }}
        >
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.75, fontWeight: 600 }}>
              {t("dcDashboard:feesEarned", { period: periodLabel })}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.02em", mt: 0.5 }}>
              {formatCurrency(earnings.periodTotal)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {t("dcDashboard:fromOrdersDelivered", {
                orders: t("boShared:orderCount", { count: earnings.periodCount }),
              })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={{ xs: 3, sm: 5 }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {t("dcDashboard:activeDrivers")}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {dashboard.driverCount}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {t("dcDashboard:inProgress")}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {dashboard.activeDeliveryCount}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {t("dcDashboard:completed")}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {dashboard.completedDeliveryCount}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Card>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {OVERVIEW_CARDS.map(({ key, labelKey, icon, accent }) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
            <HeroStat icon={icon} label={t(`dcDashboard:${labelKey}`)} value={dashboard[key]} accent={accent} />
          </Grid>
        ))}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <HeroStat
            icon={ReceiptLongOutlinedIcon}
            label={t("dcDashboard:ordersDelivered")}
            value={earnings.periodCount}
            helper={periodLabel}
            accent={brand.amber}
          />
        </Grid>
      </Grid>

      {/* Earnings by driver */}
      <Card sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 3 }}>
        <SectionHeader
          icon={PeopleAltOutlinedIcon}
          title={t("dcDashboard:byDriverTitle")}
          subtitle={t("dcDashboard:byDriverSubtitle", { period: periodLabel })}
        />
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
          <DataGrid
            autoHeight
            rows={earnings.drivers ?? []}
            getRowId={(row) => row.userId}
            columns={driverColumns}
            getRowHeight={() => "auto"}
            disableRowSelectionOnClick
            hideFooter={(earnings.drivers ?? []).length <= 100}
            sx={{ border: "none" }}
          />
          {(earnings.drivers ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
              {t("dcDashboard:noDrivers")}
            </Typography>
          )}
        </Box>
      </Card>

      {/* Earnings by store */}
      <Card sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
        <SectionHeader
          icon={StorefrontOutlinedIcon}
          title={t("dcDashboard:byStoreTitle")}
          subtitle={t("dcDashboard:byStoreSubtitle", { period: periodLabel })}
        />
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
          <DataGrid
            autoHeight
            rows={storeRows}
            getRowId={(row) => row.storeId}
            columns={storeColumns}
            hideFooter={storeRows.length <= 100}
            disableRowSelectionOnClick
            getRowClassName={(params) => (params.row.isTotalRow ? "total-row" : "")}
            sx={{
              border: "none",
              "& .total-row": {
                bgcolor: "action.hover",
                fontWeight: 700,
                "& .MuiDataGrid-cell": { fontWeight: 700 },
              },
            }}
          />
          {storeEarnings.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
              {t("dcDashboard:noStoreDeliveries")}
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
}
